/**
 * "YOU CAN'T SEE THIS" — as a written Hebrew screen. 09 §5.8, decision 8.
 *
 *   "An external customer never sees a raw 403. Every 'you can't see this' is a written Hebrew
 *    screen that says what to do next."
 *
 * Two layers, and the difference is the whole point (packages/contracts/src/decision.ts, CF-37):
 *   inside the tenant  — the first failing stage's reason, named, with one next step.
 *   outside the tenant — `reason` never arrives, and this component says exactly one uniform
 *                        sentence. It reveals nothing: not the stage, not whether the object exists.
 *
 * This component renders a decision the SERVER made. It contains no permission logic, and adding
 * any would be the defect the whole gate design exists to prevent.
 */

import type { DenyBody } from '../api/contract';
import { denialTextFor } from '../api/denialText';
import type { DenialActionKind } from '../api/denialText';

export interface DenialNoticeProps {
  /** The gate's body verbatim, as `DeniedError.body` delivered it. */
  readonly deny: DenyBody;
  /** Wired by the screen: what "next step" actually does. Absent ⇒ the step is stated, not clickable. */
  readonly onAction?: (action: DenialActionKind) => void;
}

export function DenialNotice({ deny, onAction }: DenialNoticeProps): JSX.Element {
  const text = denialTextFor(deny.reason);

  return (
    <section className="denial" data-tone={text.tone} role="alert">
      <h2 className="denial__title">{text.titleHe}</h2>
      <p className="denial__explanation">{text.explanationHe}</p>

      {onAction === undefined || text.action === 'none' ? (
        <p className="denial__next-step">{text.nextStepHe}</p>
      ) : (
        <button type="button" className="btn btn--primary" onClick={() => onAction(text.action)}>
          {text.nextStepHe}
        </button>
      )}

      {deny.requestUid === '' ? null : (
        // 09 §9.1 V3: a short reference code is the ONLY technical string a customer ever sees.
        // Never a route, never a path, never an exception type, never a status code.
        <p className="denial__reference">
          קוד לפנייה לתמיכה: <span dir="ltr">{deny.requestUid}</span>
        </p>
      )}
    </section>
  );
}
